import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { acceptCookies, declineCookies, getConsent, onConsentReset } from "../lib/cookieConsent";

export default function CookieConsentBanner() {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(() => getConsent() === null);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => onConsentReset(() => setVisible(true)), []);

  // Reserve space at the bottom of the page while the banner is showing —
  // otherwise, since it's fixed-position, it sits on top of (and blocks
  // clicks on) the footer's own links, including the one that reopens it.
  useEffect(() => {
    if (!visible || !bannerRef.current) {
      document.body.style.paddingBottom = "";
      return;
    }
    const el = bannerRef.current;
    const observer = new ResizeObserver(() => {
      document.body.style.paddingBottom = `${el.offsetHeight}px`;
    });
    observer.observe(el);
    document.body.style.paddingBottom = `${el.offsetHeight}px`;
    return () => {
      observer.disconnect();
      document.body.style.paddingBottom = "";
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      className="fixed inset-x-0 bottom-0 z-[70] border-t border-ink/10 bg-white text-ink md:border-t-0 md:bg-ink md:text-white"
    >
      <div className="container-industrial flex flex-col gap-4 py-5 md:flex-row md:items-center md:justify-between">
        <p className="max-w-2xl text-sm leading-relaxed text-steel-600 md:text-white/80">
          {t("cookieBannerText")}{" "}
          <Link
            to="/privacy-policy"
            className="underline decoration-ink/30 underline-offset-2 transition-colors duration-200 ease-engineered hover:text-accent md:decoration-white/40"
          >
            {t("cookieLearnMore")}
          </Link>
        </p>
        <div className="flex shrink-0 gap-3">
          <button
            type="button"
            onClick={() => {
              declineCookies();
              setVisible(false);
            }}
            className="border border-ink/20 px-5 py-2 text-xs uppercase tracking-wide2 text-steel-600 transition-colors duration-200 ease-engineered hover:border-ink/40 hover:text-ink md:border-white/30 md:text-white/80 md:hover:border-white/60 md:hover:text-white"
          >
            {t("cookieDecline")}
          </button>
          <button
            type="button"
            onClick={() => {
              acceptCookies();
              setVisible(false);
            }}
            className="bg-accent px-5 py-2 text-xs uppercase tracking-wide2 text-white transition-colors duration-200 ease-engineered hover:bg-accent/90"
          >
            {t("cookieAccept")}
          </button>
        </div>
      </div>
    </div>
  );
}
